import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { Users, Plus, Edit, Trash2, Shield, UserCheck, Building2, Loader2, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'editor' | 'restaurant_owner' | 'agency' | 'user';
  restaurantId?: number;
  restaurant?: {
    id: number;
    name: string;
  };
  isActive: boolean;
  createdAt: string;
}

interface UserManagementProps {}

const UserManagement: React.FC<UserManagementProps> = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/users', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      toast.error(t('user_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm(t('user_management.messages.confirm_delete'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(users.filter(u => u.id !== userId));
      toast.success(t('user_management.messages.delete_success'));
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.error || t('user_management.messages.delete_error'));
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/users/${user.id}`,
        { isActive: !user.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers(users.map(u =>
        u.id === user.id ? { ...u, isActive: !u.isActive } : u
      ));
      toast.success(user.isActive ? t('user_management.messages.toggle_success_deactivate') : t('user_management.messages.toggle_success_activate'));
    } catch (error: any) {
      console.error('Toggle error:', error);
      toast.error(error.response?.data?.error || t('user_management.messages.toggle_error'));
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-5 h-5 text-red-600" />;
      case 'agency':
        return <Building2 className="w-5 h-5 text-blue-600" />;
      case 'restaurant_owner':
        return <UserCheck className="w-5 h-5 text-green-600" />;
      case 'editor':
        return <Edit className="w-5 h-5 text-yellow-600" />;
      default:
        return <Users className="w-5 h-5 text-[#7BD5B5]/70" />;
    }
  };

  const getRoleBadge = (role: string) => {
    const badges: Record<string, { label: string; color: string }> = {
      admin: { label: t('user_management.roles.admin'), color: 'bg-red-100 text-red-800' },
      agency: { label: t('user_management.roles.agency'), color: 'bg-blue-100 text-blue-800' },
      restaurant_owner: { label: t('user_management.roles.restaurant_owner'), color: 'bg-green-100 text-green-800' },
      editor: { label: t('user_management.roles.editor'), color: 'bg-yellow-100 text-yellow-800' },
      user: { label: t('user_management.roles.user'), color: 'bg-gray-100 text-gray-800' }
    };

    const badge = badges[role] || badges.user;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7BD5B5]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0C0B]">{t('user_management.title')}</h1>
          <p className="text-[#687d76] mt-2">{t('user_management.subtitle')}</p>
        </div>
        <Link
          to="/users/create"
          className="inline-flex items-center justify-center gap-2 bg-[#0A0C0B] text-white px-4 py-3 rounded-lg font-medium hover:bg-[#1A1C1B] transition-colors w-full sm:w-auto text-center"
        >
          <Plus className="h-5 w-5" />
          {t('user_management.add_user')}
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#687d76]" />
          <input
            type="text"
            placeholder={t('user_management.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
        >
          <option value="all">{t('user_management.filters.all_roles')}</option>
          <option value="admin">{t('user_management.filters.administrator')}</option>
          <option value="agency">{t('user_management.filters.agency')}</option>
          <option value="restaurant_owner">{t('user_management.filters.restaurant_owner')}</option>
          <option value="editor">{t('user_management.filters.editor')}</option>
          <option value="user">{t('user_management.filters.user')}</option>
        </select>
      </div>

      {/* Users Table - Desktop view */}
      <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-6 font-semibold text-[#0A0C0B]">{t('user_management.table.user')}</th>
                <th className="text-left py-3 px-6 font-semibold text-[#0A0C0B]">{t('user_management.table.role')}</th>
                <th className="text-left py-3 px-6 font-semibold text-[#0A0C0B]">{t('user_management.table.restaurant')}</th>
                <th className="text-left py-3 px-6 font-semibold text-[#0A0C0B]">{t('user_management.table.status')}</th>
                <th className="text-left py-3 px-6 font-semibold text-[#0A0C0B]">{t('user_management.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#0A0C0B]/10 flex items-center justify-center">
                        {getRoleIcon(user.role)}
                      </div>
                      <div>
                        <p className="font-medium text-[#0A0C0B]">{user.name}</p>
                        <p className="text-sm text-[#687d76]">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    {getRoleBadge(user.role)}
                  </td>
                  <td className="py-4 px-6">
                    {user.restaurant ? (
                      <span className="text-[#687d76]">{user.restaurant.name}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      user.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.isActive ? t('user_management.status.active') : t('user_management.status.inactive')}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`px-3 py-1 text-xs rounded-lg ${
                          user.isActive
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {user.isActive ? t('user_management.status.deactivate') : t('user_management.status.activate')}
                      </button>
                      <Link
                        to={`/users/${user.id}/edit`}
                        className="p-2 text-[#687d76] hover:text-[#0A0C0B] hover:bg-gray-100 rounded-lg"
                        title={t('user_management.actions.edit_tooltip')}
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="p-2 text-[#687d76] hover:text-red-600 hover:bg-gray-100 rounded-lg"
                        title={t('user_management.actions.delete_tooltip')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-[#7BD5B5]/30 mx-auto mb-4" />
            <p className="text-[#687d76]">{t('user_management.messages.no_users_found')}</p>
          </div>
        )}
      </div>

      {/* Users Cards - Mobile view */}
      <div className="lg:hidden grid grid-cols-1 gap-4">
        {filteredUsers.map((user) => (
          <div key={user.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-[#0A0C0B]/10 flex items-center justify-center flex-shrink-0">
                  {getRoleIcon(user.role)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#0A0C0B] text-base truncate">{user.name}</h3>
                  <p className="text-sm text-[#687d76] truncate">{user.email}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {getRoleBadge(user.role)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.isActive ? t('user_management.status.active') : t('user_management.status.inactive')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {user.restaurant && (
              <div className="mb-3 pb-3 border-b border-gray-100">
                <p className="text-sm text-[#687d76]">
                  <span className="font-medium">Restaurant:</span> {user.restaurant.name}
                </p>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleToggleActive(user)}
                className={`px-3 py-2 text-sm rounded-lg ${
                  user.isActive
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {user.isActive ? t('user_management.status.deactivate') : t('user_management.status.activate')}
              </button>
              <Link
                to={`/users/${user.id}/edit`}
                className="px-3 py-2 text-sm bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] transition-colors"
              >
                {t('user_management.actions.edit')}
              </Link>
              <button
                onClick={() => handleDeleteUser(user.id)}
                className="px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              >
                {t('user_management.actions.delete')}
              </button>
            </div>
          </div>
        ))}
        
        {filteredUsers.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Users className="w-12 h-12 text-[#7BD5B5]/30 mx-auto mb-4" />
            <p className="text-[#687d76]">{t('user_management.messages.no_users_found')}</p>
          </div>
        )}
      </div>

     
    </div>
  );
};

export default UserManagement;

